/**
 * Vimeo Tracking Utility
 * Shopify カスタムピクセル向けに vimeo_play / vimeo_complete イベントを発火する
 */

import Vimeo from "@vimeo/player";

const trackedEvents = {};
const trackedPlayers = new WeakSet();

function isEventTracked(videoId, eventType) {
  const key = videoId + "_" + eventType;
  return !!trackedEvents[key];
}

function markEventAsTracked(videoId, eventType) {
  const key = videoId + "_" + eventType;
  trackedEvents[key] = true;
}

function publishEvent(eventName, payload) {
  if (
    window.Shopify &&
    window.Shopify.analytics &&
    typeof window.Shopify.analytics.publish === "function"
  ) {
    window.Shopify.analytics.publish(eventName, payload);
  }
}

/**
 * playerの要素とその子iframeに data-vimeo-tracked 属性を設定する
 */
function markElementAsTracked(player) {
  const el = player.element;
  if (!el) return;
  el.setAttribute("data-vimeo-tracked", "true");
  if (el.tagName !== "IFRAME") {
    const iframe = el.querySelector("iframe");
    if (iframe) {
      iframe.setAttribute("data-vimeo-tracked", "true");
    }
  }
}

/**
 * Vimeo Player インスタンスに play / ended イベントリスナーを登録する
 * @param {Object} player - Vimeo Player インスタンス
 * @param {Object} options
 * @param {boolean} options.isAutoplay - 自動再生かどうか
 * @returns {Promise} メタデータ取得・リスナー登録完了後に解決する Promise
 */
export function attachTracking(player, { isAutoplay = false } = {}) {
  if (trackedPlayers.has(player)) {
    return Promise.resolve();
  }
  trackedPlayers.add(player);
  markElementAsTracked(player);

  const autoplayStatus = isAutoplay ? "Auto play" : "Manual play";

  const titlePromise = player.getVideoTitle().catch(() => "");
  const idPromise = player.getVideoId().then(function (id) {
    return String(id);
  }).catch(() => "");

  return Promise.all([titlePromise, idPromise]).then(([videoTitle, videoId]) => {
    // ready後に再度マーク（iframe生成が遅延する場合のフォールバック）
    markElementAsTracked(player);

    player.on("play", function () {
      if (!isEventTracked(videoId, "start")) {
        markEventAsTracked(videoId, "start");
        publishEvent("vimeo_play", {
          video_name: videoTitle,
          video_id: videoId,
          autoplay: autoplayStatus,
          progress: "0%",
          page_path: window.location.pathname
        });
      }
    });

    player.on("ended", function () {
      if (!isEventTracked(videoId, "complete")) {
        markEventAsTracked(videoId, "complete");
        publishEvent("vimeo_complete", {
          video_name: videoTitle,
          video_id: videoId,
          autoplay: autoplayStatus,
          progress: "complete",
          page_path: window.location.pathname
        });
      }
    });
  });
}

/**
 * ページ上の未トラッキングVimeo iframeをスキャンしてトラッキングを付与（フォールバック）
 */
export function scanUntrackedIframes() {
  const iframes = document.querySelectorAll('iframe[src*="vimeo.com"]');
  iframes.forEach(function (iframe) {
    if (iframe.hasAttribute("data-vimeo-tracked")) {
      return;
    }
    const player = new Vimeo(iframe);
    const src = iframe.getAttribute("src") || "";
    const isAutoplay = src.includes("background=1") || src.includes("autoplay=1");
    attachTracking(player, { isAutoplay: isAutoplay });
  });
}

window.addEventListener("load", scanUntrackedIframes);
